import { useState } from "react";
import { LanguageLink } from "@/components/LanguageLink";
import { useVideoJobs } from "@/lib/hooks";
import { VideoCard } from "./components";
import { Button } from "@/components/ui/button";
import { DashboardHeader } from "@/pages/dashboard/components";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const ITEMS_PER_PAGE = 9;

function VideoListPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;
  
  const { data, isLoading, error } = useVideoJobs(ITEMS_PER_PAGE, offset);
  const { t } = useTranslation();

  const totalPages = data ? Math.ceil(data.total / ITEMS_PER_PAGE) : 0;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-lavender">
      <main className="max-w-7xl 2k:max-w-[80%] 4k:max-w-[80%] mx-auto px-6 py-8">
        <DashboardHeader />
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8 2k:mb-12 4k:mb-16">
          <div className="flex flex-col gap-1 2k:gap-3 4k:gap-5">
            <h1 className="font-h2 2k:text-4xl 4k:text-6xl">{t("videoList.title")}</h1>
            <p className="font-body text-muted-foreground mt-1 2k:text-lg 4k:text-2xl">
              {t("videoList.subtitle")}
            </p>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4 2k:gap-8 4k:gap-12">
              <div className="w-8 h-8 2k:w-16 2k:h-16 4k:w-24 4k:h-24 border-2 border-rose-dark border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground 2k:text-xl 4k:text-3xl">{t("videoList.loading")}</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="text-center py-12">
            <p className="text-destructive">
              {t("videoList.error")}
            </p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && data?.jobs && data.jobs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4 2k:text-2xl 4k:text-4xl 2k:mb-8 4k:mb-12">
              {t("videoList.noVideos")}
            </p>
            <LanguageLink to="/dashboard">
              <Button>{t("videoList.createFirst")}</Button>
            </LanguageLink>
          </div>
        )}

        {/* Video list */}
        {!isLoading && data?.jobs && data.jobs.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2k:grid-cols-4 4k:grid-cols-4 gap-6 2k:gap-8 4k:gap-12">
              {data.jobs.map((job) => (
                <VideoCard key={job.id} video={job} />
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-12 pb-8 2k:mt-20 2k:gap-6 4k:mt-28 4k:gap-10">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="rounded-full 2k:h-10 2k:w-10 4k:h-16 4k:w-16"
                >
                  <ChevronLeft className="h-4 w-4 2k:h-5 2k:w-5 4k:h-8 4k:w-8" />
                </Button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium 2k:text-lg 4k:text-2xl">
                    {t("videoList.pageOf", { current: currentPage, total: totalPages })}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="rounded-full 2k:h-10 2k:w-10 4k:h-16 4k:w-16"
                >
                  <ChevronRight className="h-4 w-4 2k:h-5 2k:w-5 4k:h-8 4k:w-8" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export { VideoListPage };
